import merge from "lodash/merge";
import { HighchartsSankeyChartContainer } from "../../../common/highcharts-sankey-chart-container/highcharts-sankey-chart-container";
import { useEffect, useMemo, useState } from "react";
import { WORKFLOW_STATUS_COLOR_TYPES } from "@enums/workflow-status-color-types";
import { BASIC_HIGHCHARTS_COLORS, DEFAULT_HIGHCHARTS_OPTIONS } from "@constants/highcharts";

type WorkflowSankeyDiagramProps = {
    title: string;
    data: Array<[string, string, number]>;
};

export const WorkflowSankeyDiagram = (props: WorkflowSankeyDiagramProps) => {
    const { title, data } = props;

    const getStatusColor = (toKeyStr: string) => {
        const status = (toKeyStr || "").toLowerCase().trim();
        let color;
        if (status && ['abandoned', 'denied', 'approved'].includes(status)) {
            switch (status) {
                case 'abandoned':
                    color = WORKFLOW_STATUS_COLOR_TYPES.ABANDONED;
                    break;
                case 'denied':
                    color = WORKFLOW_STATUS_COLOR_TYPES.DENIED;
                    break;
                case 'approved':
                    color = WORKFLOW_STATUS_COLOR_TYPES.APPROVED;
                    break;
            }
        } else {
            color = WORKFLOW_STATUS_COLOR_TYPES.OBLIGATION;
        }

        return color;
    };

    const sankeyData = useMemo(() => {
        return data.map(entry => {
            const [from, to, weight] = entry;
            const sankeyNode = {
                from,
                to,
                weight,
                color: getStatusColor(to)
            };

            return sankeyNode;
        });
    }, [data]);

    const seriesColors = useMemo(() => {
        let colorIndex = 0;
        const colorMap: { [key: string]: string } = {};
        return sankeyData.reduce((list, point) => {
            const toValue = point.to.toLowerCase().trim();
            const isToValueAStatusKey = ['abandoned', 'denied', 'approved'].includes(toValue);
            let toKeyColor;
            if (!colorMap[point.from]) {
                // We don't have a color for this point yet
                const fromKeyColor = BASIC_HIGHCHARTS_COLORS[colorIndex];
                colorMap[point.from] = fromKeyColor;
                list.push(fromKeyColor);
                colorIndex++;

                if (isToValueAStatusKey) {
                    toKeyColor = getStatusColor(point.to);
                    colorMap[point.to] = toKeyColor;
                    list.push(getStatusColor(point.to));
                } else {
                    toKeyColor = WORKFLOW_STATUS_COLOR_TYPES.OBLIGATION;
                    colorMap[point.to] = toKeyColor;
                    list.push(toKeyColor);
                    colorIndex++;
                }
            } else {
                // We have a color for this point
                if (isToValueAStatusKey) {
                    toKeyColor = getStatusColor(point.to);
                } else {
                    toKeyColor = WORKFLOW_STATUS_COLOR_TYPES.OBLIGATION;
                }
                colorMap[point.to] = toKeyColor;
                list.push(toKeyColor);
                colorIndex++;
            }
            return list;
        }, []);
    }, [sankeyData]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_OPTIONS, {
        chart: {
            type: "sankey",
            events: {
                load: function () {
                    this.reflow();
                }
            }
        },
        title: {
            text: title,
        },
        plotOptions: {
            sankey: {
                linkOpacity: 1
            }
        },
        series: [{
            keys: ["from", "to", "weight"],
            data: sankeyData,
            type: "sankey",
            name: title,
            colors: seriesColors
        }]
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = {
            ...chartOptions,
            title: {
                ...chartOptions.title,
                text: title
            },
            series: [{
                keys: ["from", "to", "weight"],
                data: sankeyData,
                type: "sankey",
                name: title,
                colors: seriesColors
            }]
        };

        setChartOptions(newChartOptions);

    }, [title, sankeyData, seriesColors]);

    return <HighchartsSankeyChartContainer options={chartOptions} />;
};